import type { IntegrationView } from "../api/integrations";
import type { ChangeInfo, ProviderConfig, ProviderName } from "../api/types";

/** The server's summary model when none is set (`DEFAULT_SUMMARY_MODEL` in crates/xai). */
export const DEFAULT_SUMMARY_MODEL = "grok-4.20-0309-non-reasoning";

export const PROVIDER_ORDER = [
  "telnyx",
  "xai",
  "xai_research",
  "brave",
  "storage",
  "email",
  "anthropic",
] as const satisfies readonly ProviderName[];

export interface FieldDef {
  key: string;
  label: string;
  help?: string;
  mono?: boolean;
  placeholder?: string;
  /** Optional keys can be removed once set. */
  optional?: boolean;
}

/**
 * Field names match `ProviderKind::secret_fields` and `config_fields` in crates/api-types.
 * The Telnyx webhook public key is a setting there: it is public, and the server returns it.
 */
export const PROVIDER_FIELDS: Record<ProviderName, { secrets: FieldDef[]; settings: FieldDef[] }> = {
  telnyx: {
    secrets: [{ key: "api_key", label: "API key", help: "Telnyx portal, Keys and Credentials, API Keys." }],
    settings: [
      {
        key: "public_key",
        label: "Webhook public key",
        mono: true,
        help: "Checks that webhooks really come from Telnyx. Telnyx portal, Keys and Credentials, Public Key.",
      },
      {
        key: "connection_id",
        label: "Credential connection ID",
        mono: true,
        help: "The SIP connection the desktop app's calling credentials belong to.",
      },
      { key: "call_control_app_id", label: "Call Control app ID", mono: true },
      {
        key: "coaching_connection_id",
        label: "Parking connection ID",
        mono: true,
        help: "A second credential connection with outbound call parking turned on. Reps sign in on it so every outbound call goes through the server: Transfer, Add person and listening then work on every call. Needed only when outbound mode is direct.",
      },
      { key: "outbound_voice_profile_id", label: "Outbound voice profile ID", mono: true },
      {
        key: "messaging_profile_id",
        label: "Default messaging profile ID",
        mono: true,
        help: "Brands can set their own messaging profile under Numbers and brands.",
      },
    ],
  },
  xai: {
    secrets: [
      { key: "api_key", label: "API key", help: "console.x.ai, API Keys." },
      {
        key: "management_key",
        label: "Management key (optional)",
        optional: true,
        help: "console.x.ai, Settings, Management Keys, read-only billing. Lets Costs show what xAI billed beside what Vertec metered.",
      },
    ],
    settings: [
      {
        key: "summary_model",
        label: "Summary model",
        mono: true,
        placeholder: DEFAULT_SUMMARY_MODEL,
        help: `Leave empty to use ${DEFAULT_SUMMARY_MODEL}.`,
      },
      {
        key: "team_id",
        label: "Team ID (optional)",
        mono: true,
        help: "console.x.ai, Settings, Team. Needed with the management key.",
      },
    ],
  },
  xai_research: {
    secrets: [
      {
        key: "api_key",
        label: "API key",
        help: "A separate key from console.x.ai, so lead generation has its own spend limit at xAI.",
      },
    ],
    settings: [],
  },
  brave: {
    secrets: [
      {
        key: "api_key",
        label: "API key",
        help: "api-dashboard.search.brave.com, API Keys. Without it, web search uses xAI and map listings use OpenStreetMap.",
        optional: true,
      },
    ],
    settings: [],
  },
  storage: {
    secrets: [
      { key: "access_key_id", label: "Access key ID" },
      { key: "secret_access_key", label: "Secret access key" },
    ],
    settings: [
      { key: "endpoint", label: "S3 endpoint", mono: true, placeholder: "https://s3.example.com" },
      { key: "region", label: "Region", mono: true },
      { key: "bucket", label: "Bucket", mono: true },
    ],
  },
  email: {
    secrets: [
      {
        key: "client_secret",
        label: "Client secret",
        help: "From the Vertec Voice Mailer app registration. tools/entra/New-VertecVoiceMailer.ps1 creates it.",
      },
    ],
    settings: [
      { key: "tenant_id", label: "Directory (tenant) ID", mono: true },
      { key: "client_id", label: "Application (client) ID", mono: true },
      {
        key: "sender",
        label: "Sender mailbox",
        mono: true,
        placeholder: "voice@vertec.io",
        help: "The app may send only as this mailbox (Exchange RBAC for Applications).",
      },
      {
        key: "bug_report_recipients",
        label: "Problem report recipients",
        placeholder: "apino@vertec.io",
        help: "Comma separated. Empty means apino@vertec.io.",
      },
    ],
  },
  anthropic: {
    secrets: [{ key: "api_key", label: "API key", help: "platform.claude.com, Settings, API Keys." }],
    settings: [],
  },
};

export type ProviderStatus = "connected" | "attention" | "not_set_up";

export const STATUS_LABEL: Record<ProviderStatus, string> = {
  connected: "Connected",
  attention: "Needs attention",
  not_set_up: "Not set up",
};

/**
 * Connected means the last test passed. A provider with keys that failed, degraded or was
 * never tested needs attention. One with no keys is not set up.
 */
export function providerStatus(c: Pick<ProviderConfig, "configured" | "health" | "secrets">): ProviderStatus {
  const anySecret = Object.values(c.secrets).some((s) => s.set);
  if (!c.configured && !anySecret) return "not_set_up";
  if (c.configured && c.health.status === "ok") return "connected";
  return "attention";
}

/** The one line of real configuration a card shows under its role. */
export function providerSummary(name: ProviderName, c: ProviderConfig): string | null {
  const s = c.settings;
  switch (name) {
    case "telnyx":
      return c.secrets.api_key?.last_four ? `Key •••• ${c.secrets.api_key.last_four}` : null;
    case "xai":
      return s.summary_model?.trim() || DEFAULT_SUMMARY_MODEL;
    case "xai_research":
    case "brave":
    case "anthropic":
      return c.secrets.api_key?.last_four ? `Key •••• ${c.secrets.api_key.last_four}` : null;
    case "storage":
      return s.bucket?.trim() || null;
    case "email":
      return s.sender?.trim() || null;
  }
}

/** Who uses an integration's keys, one line on its sheet. */
export const USED_BY: Record<ProviderName, string> = {
  telnyx: "Calls, texts, numbers and live transcription.",
  xai: "Summaries, the Assistant and problem report triage (sent to the triage worker with each report).",
  xai_research:
    "Lead generation and import checks. Sent to the lead generation worker with each run. Never stored there.",
  brave:
    "Web and map search for lead generation. Sent to the lead generation worker with each run. Never stored there.",
  storage: "Call recordings, voicemail and backups.",
  email: "Problem report emails and invitations.",
  anthropic: "The Assistant's new engine, for the members it is turned on for in Assistant settings.",
};

/** "Changed by Ana Ruiz, Oct 12" or "Never changed". */
export function changedLine(c: ChangeInfo | null | undefined): string {
  if (!c) return "Never changed";
  const when = new Date(c.at).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return c.by ? `Changed by ${c.by.name}, ${when}` : `Changed on the server, ${when}`;
}

/** The Odoo card's pill: set up and tested, set up and failing or untested, or not set up. */
export function odooStatus(v: Pick<IntegrationView, "configured" | "health">): ProviderStatus {
  if (!v.configured) return "not_set_up";
  return v.health.status === "ok" ? "connected" : "attention";
}
